'use client';

import useSWR from 'swr';
import {
  SUBFLOW_PRODUCT,
  SUBFLOW_SOURCE,
} from '@/components/subflow-v3/components/utils';
import {
  ChildInfo,
  postFetcher,
} from '@/components/subflow-v3/context/SubFlowV3Context';

export interface OrderPreview {
  uuid: null;
  recipient_uuid: null;
  purchaser_uuids: null;
  child_uuid: null;
  delivery_address_uuid: null;
  billing_address_uuid: null;
  subscription_status: string;
  subscription_type: string;
  subscription_source: string;
  expiration_date: null;
  cancellation_reason: null;
  cancel_reason_comment: null;
  notes: Notes;
  error_message: null;
  error_type: null;
  billing_error_count: null;
  auto_extensions_enabled: boolean;
  partial_subscription: boolean;
  bundle_discount_uuid: null;
  next_billing_date: null;
  next_kit_name: null;
  created_at: null;
  updated_at: null;
  is_babylist: boolean;
  schedule_sections: ScheduleSection[];
  addon_preferences: any[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Notes {}

export interface ScheduleSection {
  uuid: null;
  section_type: string;
  section_status: string;
  section_source: string;
  activation_code: null;
  created_at: null;
  updated_at: null;
  scheduled_items: ScheduledItem[];
  subscription_preference: SubscriptionPreference;
}

export interface ScheduledItem {
  uuid: null;
  product_uuid: string;
  scheduled_type: ScheduledType;
  process_status: ProcessStatus;
  scheduled_date: Date | null;
  billing_status: BillingStatus;
  billing_date: null;
  delivery_status: DeliveryStatus;
  shipping_date: null;
  processed_date: null;
  order_uuid: null;
  invoice_uuid: null;
  original_invoice_uuid: null;
  retry_date: null;
  retry_count: null;
  billing_error_message: null;
  shipping_error_message: null;
  skip_reason: null;
  notes: null;
  original_scheduled_date: Date | null;
  order_number: null;
  created_at: null;
  updated_at: null;
  subscription_uuid: null;
  subscription_variant_uuid: string;
  variant_uuid: string;
  user_uuid: null;
  scheduled_addons: any[];
  delight_item_addons: any[];
}

export enum BillingStatus {
  AwaitingPayment = 'awaiting_payment',
}

export enum DeliveryStatus {
  NotShipped = 'not_shipped',
}

export enum ProcessStatus {
  AwaitingProcess = 'awaiting_process',
  NotApplicable = 'not_applicable',
}

export enum ScheduledType {
  InitialItem = 'initial_item',
  PreviousPlay = 'previous_play',
  Scheduled = 'scheduled',
}

export interface SubscriptionPreference {
  product_choices_uuids: any[];
  subscription_variant_uuid: string;
  subscription_product_anchor: string;
  selected_options: Notes;
  created_at: null;
  updated_at: null;
}

export const useOrderPreview = (childInfo: Partial<ChildInfo>) => {
  const body = {
    birth_date: `${childInfo.year}-${childInfo.month}-${childInfo.day}`,
    source: SUBFLOW_SOURCE,
    subscription_product: SUBFLOW_PRODUCT,
  };

  const hasFullPaylod = childInfo.year && childInfo.month && childInfo.day;

  return useSWR<OrderPreview>(
    hasFullPaylod
      ? [`/api/v1/subscription/preview`, JSON.stringify(body)]
      : null,
    postFetcher,
    { revalidateOnFocus: false },
  );
};
